/**
 * The "Check for updates" control for the Settings App section. Shows the running
 * version and lets the user check on demand; if a newer build is found, the same
 * download-and-install flow the launch prompt uses is offered right here.
 *
 * On web/dev there's nothing to install, so a check just reports the current
 * version as up to date — no error, no dead button.
 *
 * The version label doubles as a DISCREET admin switch: tapping it seven times
 * in quick succession flips the persisted adminMode (src/store/admin.ts). That's
 * how the web-only "rebuild & reload" control is unlocked, tucked away here so a
 * student never trips it by accident. A soft gate suitable while the app is
 * served only over Edd's private Tailscale / as the APK; real admin auth should
 * replace it when sign-in lands.
 */
import { useRef } from 'react';
import { useUpdateStore } from '@/store/update';
import { useAdminStore } from '@/store/admin';
import { useShellStore } from '@/store/shell';
import { canInstallApk } from '@/update/apkDownload';
import { haptic } from '@/ui/haptics';

const TAPS_TO_TOGGLE = 7;
const TAP_WINDOW_MS = 1_200;

function mbFrac(bytes: number): string {
  return (bytes / 1_000_000).toFixed(1);
}

export function UpdateSettings() {
  const checkStatus = useUpdateStore((s) => s.checkStatus);
  const installStatus = useUpdateStore((s) => s.installStatus);
  const manifest = useUpdateStore((s) => s.manifest);
  const versionName = useUpdateStore((s) => s.currentVersionName);
  const progress = useUpdateStore((s) => s.progress);
  const bytes = useUpdateStore((s) => s.bytes);
  const total = useUpdateStore((s) => s.total);
  const error = useUpdateStore((s) => s.error);
  const check = useUpdateStore((s) => s.check);
  const startUpdate = useUpdateStore((s) => s.startUpdate);

  const adminMode = useAdminStore((s) => s.adminMode);
  const toggleAdminMode = useAdminStore((s) => s.toggleAdminMode);
  const showToast = useShellStore((s) => s.showToast);

  // Tap-count gesture on the version label: seven quick taps flip admin mode.
  const taps = useRef(0);
  const lastTap = useRef(0);
  const onVersionTap = () => {
    const now = Date.now();
    taps.current = now - lastTap.current < TAP_WINDOW_MS ? taps.current + 1 : 1;
    lastTap.current = now;
    if (taps.current >= TAPS_TO_TOGGLE) {
      taps.current = 0;
      toggleAdminMode();
      haptic('commit');
      showToast(adminMode ? 'Admin mode off' : 'Admin mode on');
    }
  };

  const checking = checkStatus === 'checking';
  const available = checkStatus === 'available' && manifest != null;
  const busy = installStatus === 'downloading' || installStatus === 'installing';
  const pct = Math.round(progress * 100);
  const bytesLine = total > 0 ? `${mbFrac(bytes)} / ${mbFrac(total)} MB · ${pct}%` : `${mbFrac(bytes)} MB`;

  return (
    <>
      <label
        className="acp-fieldlabel"
        style={{ marginTop: 14, cursor: 'default', userSelect: 'none' }}
        onClick={onVersionTap}
      >
        Version {versionName}
        {adminMode && <span className="acp-admin-flag"> · admin</span>}
      </label>

      {!available ? (
        <button
          type="button"
          className="acp-btn"
          style={{ marginTop: 6 }}
          disabled={checking}
          onClick={() => { haptic('tick'); void check(); }}
        >
          {checking ? 'Checking…' : 'Check for updates'}
        </button>
      ) : (
        <button
          type="button"
          className="acp-btn on"
          style={{ marginTop: 6 }}
          disabled={busy}
          onClick={() => { haptic('tick'); void startUpdate(); }}
        >
          {installStatus === 'downloading'
            ? bytesLine
            : installStatus === 'installing'
              ? 'Installing…'
              : `Update to ${manifest?.versionName}`}
        </button>
      )}

      {checkStatus === 'upToDate' && (
        <p className="acp-note" style={{ marginTop: 4 }}>
          {canInstallApk() ? 'We’re on the latest version.' : 'Updates install from the app on the device.'}
        </p>
      )}
      {checkStatus === 'error' && (
        <p className="acp-note" style={{ marginTop: 4, color: 'var(--red)' }}>
          {error ?? 'Couldn’t check for updates.'}
        </p>
      )}
      {available && manifest?.notes && installStatus === 'idle' && (
        <p className="acp-note" style={{ marginTop: 4 }}>{manifest.notes}</p>
      )}
      {installStatus === 'installing' && (
        <p className="acp-note" style={{ marginTop: 4 }}>
          Opening the installer… follow the on-screen prompts to finish.
        </p>
      )}
      {installStatus === 'error' && (
        <p className="acp-note" style={{ marginTop: 4, color: 'var(--red)' }}>{error}</p>
      )}
      {adminMode && (
        <p className="acp-note" style={{ marginTop: 4 }}>
          Admin mode is on. On the desktop-served web app, an “update available” control appears when the PC is
          behind the latest pushed code. Tap the version seven times to turn admin off.
        </p>
      )}
    </>
  );
}
